import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost, adminCompanyPath } from '../../lib/api-client';
import { useCompany } from '../../contexts/CompanyContext';
import { useTier } from '../../hooks/useTier';
import { StatCard } from '../../components/ui/stat-card';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '../../components/ui/table';
import { Button } from '../../components/ui/button';

interface SellerItem {
  id: string;
  code: string;
  name: string;
  payinFeePercent: number;
  payoutFeePercent: number;
}

interface StatRow {
  deposit: number;
  topup: number;
  withdraw: number;
  settle: number;
}

const MAX_ROWS = 10;

function pad(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function fmt(n: number): string {
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function SellerStatRow({
  seller,
  companyId,
  dateFrom,
  dateTo,
  syncDate,
}: {
  seller: SellerItem;
  companyId: string;
  dateFrom: string;
  dateTo: string;
  syncDate: string;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'dash-seller-stats', seller.id, dateFrom, dateTo],
    queryFn: () =>
      apiGet<{ items: StatRow[] }>(adminCompanyPath(`/sellers/${seller.id}/stats`, companyId), {
        dateFrom,
        dateTo,
        granularity: 'daily',
        pageSize: '100',
      }),
  });

  const syncMutation = useMutation({
    mutationFn: () =>
      apiPost(adminCompanyPath(`/sync/stats/${seller.id}`, companyId), {
        queryParams: { date: syncDate },
      }),
    onSuccess: (res) => {
      if (res.ok) {
        queryClient.invalidateQueries({
          queryKey: ['admin', 'dash-seller-stats', seller.id],
        });
      }
    },
  });

  const items = data?.ok ? data.data.items : [];
  const deposit = items.reduce((s, r) => s + r.deposit, 0);
  const topup = items.reduce((s, r) => s + r.topup, 0);
  const withdraw = items.reduce((s, r) => s + r.withdraw, 0);
  const settle = items.reduce((s, r) => s + r.settle, 0);
  const payinFee = ((deposit + topup) * seller.payinFeePercent) / 100;
  const payoutFee = ((withdraw + settle) * seller.payoutFeePercent) / 100;
  const totalFee = payinFee + payoutFee;

  const show = (v: string) => (isLoading ? '…' : v);

  return (
    <TableRow>
      <TableCell className="font-medium text-gray-900 dark:text-gray-100">
        {seller.name}
        <span className="ml-1.5 font-mono text-xs text-gray-400">{seller.code}</span>
      </TableCell>
      <TableCell className="text-right">{show(fmt(deposit))}</TableCell>
      <TableCell className="text-right">{show(fmt(topup))}</TableCell>
      <TableCell className="text-right">{show(fmt(withdraw))}</TableCell>
      <TableCell className="text-right">{show(fmt(settle))}</TableCell>
      <TableCell className="text-right text-primary">
        {show(fmt(payinFee))}
        <span className="ml-1 text-[0.65rem] text-gray-400">{seller.payinFeePercent}%</span>
      </TableCell>
      <TableCell className="text-right text-emerald-600 dark:text-emerald-400">
        {show(fmt(payoutFee))}
        <span className="ml-1 text-[0.65rem] text-gray-400">{seller.payoutFeePercent}%</span>
      </TableCell>
      <TableCell className="text-right font-semibold text-gray-900 dark:text-gray-100">
        {show(fmt(totalFee))}
      </TableCell>
      <TableCell className="text-right">
        <Button
          size="sm"
          variant="outline"
          onClick={() => syncMutation.mutate()}
          disabled={syncMutation.isPending}
        >
          {syncMutation.isPending ? t('common.loading') : t('admin.syncNow')}
        </Button>
      </TableCell>
    </TableRow>
  );
}

export function AdminDashboard() {
  const { t } = useTranslation();
  const { activeCompanyId, activeCompany } = useCompany();
  const { isSuper } = useTier();
  const [page, setPage] = useState(0);

  // Reset to the first page when the active company changes.
  useEffect(() => {
    setPage(0);
  }, [activeCompanyId]);

  const { data: sellersData } = useQuery({
    queryKey: ['admin', 'sellers', activeCompanyId],
    queryFn: () =>
      apiGet<{ items: SellerItem[]; total: number }>(
        adminCompanyPath('/sellers', activeCompanyId!),
      ),
    enabled: !!activeCompanyId,
  });

  // API config is superadmin-only on the backend; scoped admins skip it.
  const { data: configData } = useQuery({
    queryKey: ['admin', 'api-config', activeCompanyId],
    queryFn: () =>
      apiGet<{ lastSyncAt: string | null; apiUrl: string } | null>(
        adminCompanyPath('/api-config', activeCompanyId!),
      ),
    enabled: !!activeCompanyId && isSuper,
  });

  if (!activeCompanyId) {
    return (
      <div className="space-y-6">
        <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-amber-800 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200">
          {t('company.noCompanySelected')}
        </div>
      </div>
    );
  }

  const sellers = sellersData?.ok ? sellersData.data.items : [];
  const sellerCount = sellersData?.ok ? sellersData.data.total : 0;
  const lastSync = configData?.ok && configData.data ? configData.data.lastSyncAt : null;

  // Current month only (start of month → today), GMT+7 for the sync date.
  const now = new Date();
  const ym = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const dateFrom = `${ym}-01`;
  const dateTo = pad(now);
  const syncDate = new Date(`${ym}-01T00:00:00.000+07:00`).toISOString();
  const monthLabel = now.toLocaleDateString(undefined, { year: 'numeric', month: 'long' });

  const pageCount = Math.max(1, Math.ceil(sellers.length / MAX_ROWS));
  const safePage = Math.min(page, pageCount - 1);
  const pageSellers = sellers.slice(safePage * MAX_ROWS, safePage * MAX_ROWS + MAX_ROWS);

  return (
    <div className="space-y-6">
      {activeCompany && (
        <p className="text-gray-500 dark:text-gray-400">
          {activeCompany.name}{' '}
          <span className="font-mono text-xs text-gray-400">({activeCompany.code})</span>
        </p>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <StatCard label={t('dashboard.activeSellers')} value={sellerCount} icon="S" />

        {isSuper && (
          <>
            <StatCard
              label={t('admin.lastSync')}
              value={lastSync ? new Date(lastSync).toLocaleDateString() : t('admin.neverSynced')}
              hint={lastSync ? new Date(lastSync).toLocaleTimeString() : undefined}
              icon="↻"
              accentClass="bg-gradient-to-tr from-violet-600 to-fuchsia-500"
            />
            <StatCard
              label={t('admin.apiStatus')}
              value={
                configData?.ok && configData.data ? t('admin.configured') : t('admin.notConfigured')
              }
              icon="◈"
              accentClass={
                configData?.ok && configData.data
                  ? 'bg-gradient-to-tr from-emerald-600 to-emerald-400'
                  : 'bg-gradient-to-tr from-amber-500 to-amber-400'
              }
            />
          </>
        )}
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle>{t('dashboard.sellersOverview')}</CardTitle>
            <span className="text-xs font-medium text-gray-400">
              {monthLabel} · {dateFrom} ~ {dateTo}
            </span>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {pageSellers.length === 0 ? (
            <div className="p-6 text-gray-400">{t('common.noData')}</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('sellers.name')}</TableHead>
                  <TableHead className="text-right">{t('transactions.deposit')}</TableHead>
                  <TableHead className="text-right">{t('transactions.topup')}</TableHead>
                  <TableHead className="text-right">{t('transactions.withdraw')}</TableHead>
                  <TableHead className="text-right">{t('transactions.settle')}</TableHead>
                  <TableHead className="text-right">{t('transactions.calcPayinFee')}</TableHead>
                  <TableHead className="text-right">{t('transactions.calcPayoutFee')}</TableHead>
                  <TableHead className="text-right">{t('dashboard.totalFees')}</TableHead>
                  <TableHead className="text-right">{t('sellers.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pageSellers.map((s) => (
                  <SellerStatRow
                    key={s.id}
                    seller={s}
                    companyId={activeCompanyId}
                    dateFrom={dateFrom}
                    dateTo={dateTo}
                    syncDate={syncDate}
                  />
                ))}
              </TableBody>
            </Table>
          )}
          {pageCount > 1 && (
            <div className="flex items-center justify-between gap-3 border-t border-gray-100 px-5 py-3 dark:border-gray-700/60">
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {t('common.pageOf', { page: safePage + 1, total: pageCount })}
              </span>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={safePage === 0}
                >
                  {t('common.previous')}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                  disabled={safePage >= pageCount - 1}
                >
                  {t('common.next')}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
